import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ForcePasswordChange from "./ForcePasswordChange";
import NotificationBell from "./NotificationBell";
import { PLATFORM_NAME } from "../lib/useBrand";
import {
  ChartBar,
  ChartLine,
  Users,
  Buildings,
  Funnel,
  SignOut,
  Lightning,
  PlugsConnected,
  PhoneCall,
  ShieldCheck,
  WhatsappLogo,
  MapPin,
  Lock,
  TrendUp,
  Megaphone,
  ArrowsClockwise,
  CalendarCheck,
  CopySimple,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/", label: "Dashboard", icon: ChartBar, end: true, testid: "nav-dashboard" },
  // Second by design: the call queue is where an agent starts their shift, so
  // it sits directly under Dashboard and above the raw lead list. No `perm` or
  // `adminOnly` — everyone who works leads has a day to work.
  { to: "/my-day", label: "My Day", icon: CalendarCheck, testid: "nav-my-day" },
  { to: "/leads", label: "Leads", icon: Funnel, testid: "nav-leads" },
  { to: "/duplicates", label: "Duplicates", icon: CopySimple, testid: "nav-duplicates", adminOnly: true },
  { to: "/sources", label: "Sources", icon: TrendUp, testid: "nav-sources" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/callyzer", label: "Callyzer", icon: PhoneCall, testid: "nav-callyzer", perm: "can_view_callyzer" },
  { to: "/kraya", label: "Kraya", icon: WhatsappLogo, testid: "nav-kraya", perm: "can_view_kraya" },
  { to: "/reports", label: "Reports", icon: ChartLine, testid: "nav-reports", perm: "can_view_reports" },
  { to: "/branches", label: "Branches", icon: Buildings, testid: "nav-branches", adminOnly: true },
  { to: "/routing", label: "Routing", icon: MapPin, testid: "nav-routing", perm: "can_view_routing", adminOnly: true },
  { to: "/agents", label: "Agents", icon: Users, testid: "nav-agents", adminOnly: true },
  { to: "/permissions", label: "Permissions", icon: Lock, testid: "nav-permissions", adminOnly: true },
  { to: "/security", label: "Security", icon: ShieldCheck, testid: "nav-security", perm: "can_view_security", adminOnly: true },
  { to: "/webhooks", label: "Webhooks", icon: PlugsConnected, testid: "nav-webhooks", perm: "can_view_webhooks", adminOnly: true },
  { to: "/conversions", label: "Conversions", icon: ArrowsClockwise, testid: "nav-conversions", adminOnly: true },
  // Platform-level: the combined owner view across every brand.
  { to: "/businesses", label: "All Businesses", icon: Buildings, testid: "nav-businesses", superAdminOnly: true },
];

export default function AppLayout() {
  const { user, logout, businesses, switchBusiness } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const current = businesses.find((b) => b.slug === user?.business);
  const brandName = current?.branding?.short_name || user?.business_name || PLATFORM_NAME;
  const brandColor = current?.branding?.primary_color || "";
  const brandLogo = current?.branding?.logo_url || "";

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-border bg-white flex flex-col" data-testid="app-sidebar">
        <div className="h-16 px-5 flex items-center border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-7 h-7 shrink-0 bg-primary text-primary-foreground flex items-center justify-center overflow-hidden"
              style={brandColor ? { backgroundColor: brandColor } : undefined}
            >
              {brandLogo ? (
                <img src={brandLogo} alt="" className="w-full h-full object-cover" />
              ) : (
                <Lightning size={16} weight="fill" />
              )}
            </div>
            <div className="min-w-0">
              <div
                className="font-display font-bold text-base leading-none truncate"
                data-testid="brand-name"
                title={brandName}
              >
                {brandName}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Ops Console</div>
            </div>
          </div>
        </div>

        {/* Business switcher — super admins only. Staff belong to exactly one
            brand, so showing them a one-item dropdown would only add noise. */}
        {user?.is_super_admin && businesses.length > 1 && (
          <div className="px-3 py-2.5 border-b border-border">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-1.5">
              Business
            </label>
            <select
              value={user.business || ""}
              onChange={(e) => switchBusiness(e.target.value)}
              data-testid="business-switcher"
              className="w-full text-sm border border-border bg-white px-2 py-1.5 focus:outline-none focus:border-primary"
            >
              {businesses.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {b.branding?.short_name || b.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {navItems.filter((i) => {
            // Platform-level entries are for super admins only — a brand admin
            // is still an "admin", so this check must come before the bypass.
            if (i.superAdminOnly) return Boolean(user?.is_super_admin);
            // Admin sees every nav. Non-admins filtered by adminOnly or perm flag.
            if (user?.role === "admin") return true;
            if (i.adminOnly) return false;
            if (i.perm && !user?.permissions?.[i.perm]) return false;
            return true;
          }).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "border-primary bg-primary/5 text-primary font-semibold"
                    : "border-transparent text-foreground/70 hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <item.icon size={18} weight="regular" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 bg-secondary flex items-center justify-center text-xs font-bold uppercase">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{user?.role}</div>
            </div>
            <NotificationBell />
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-secondary transition-colors"
              title="Sign out"
              data-testid="logout-button"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 relative">
        {/* Anti-screenshot watermark — non-admins only */}
        {user && user.role !== "admin" && <ScreenWatermark email={user.email} />}
        <Outlet />
        <ForcePasswordChange />
      </main>
    </div>
  );
}

// Diagonal repeating watermark showing the agent's email + timestamp.
// pointer-events-none + text-[8px] means it never blocks UI but shows
// up in every screenshot/screen-share — creates deterrent against
// "just take a photo of the leads list and send to competitor".
function ScreenWatermark({ email }) {
  const [now] = React.useState(() => new Date().toLocaleString());
  const text = `${email || "user"} · ${now}`;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden select-none"
      style={{
        opacity: 0.06,
        color: "#000",
        fontFamily: "monospace",
        fontSize: "10px",
        letterSpacing: "0.05em",
        transform: "rotate(-24deg)",
        transformOrigin: "center",
        whiteSpace: "nowrap",
      }}
      data-testid="screen-watermark"
    >
      {Array.from({ length: 60 }).map((_, i) => (
        <div key={i} style={{ padding: "1.5rem 0", textAlign: "center" }}>
          {Array.from({ length: 16 }).map((__, j) => (
            <span key={j} style={{ marginRight: "3rem" }}>{text}</span>
          ))}
        </div>
      ))}
    </div>
  );
}
